import React, { useState, useEffect } from 'react';
import {
  BarChart3,
  TrendingUp,
  TrendingDown,
  Award,
  Loader2,
  Users,
  Sparkles,
  AlertTriangle,
  BookOpen,
  Target,
  GraduationCap,
  Palette,
  Film
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Subject display names and colors
const SUBJECT_CONFIG = {
  'filosofia': { name: 'Filosofía', color: '#3b82f6', icon: BookOpen },
  'historia-arte': { name: 'Historia del Arte', color: '#f59e0b', icon: Palette },
  'cultura-audiovisual': { name: 'Cultura Audiovisual', color: '#10b981', icon: Film }
};

// Group colors (A=cyan, B=purple)
const GROUP_COLORS = {
  'A': '#06b6d4',
  'B': '#8b5cf6',
  'C': '#ec4899'
};

// Subject Toggle Component - Matches profesor theme
const SubjectToggle = ({ selectedSubject, onSelect }) => {
  const options = [
    { id: null, name: 'Todas' },
    { id: 'filosofia', name: 'Filosofía' },
    { id: 'historia-arte', name: 'Hist. Arte' },
    { id: 'cultura-audiovisual', name: 'Cult. Aud.' }
  ];

  return (
    <div className="bg-gray-800/50 rounded-xl p-1 flex flex-wrap sm:flex-nowrap gap-1 border border-gray-700/50" data-testid="subject-toggle">
      {options.map((option) => {
        const isSelected = selectedSubject === option.id;
        
        return (
          <button
            key={option.id || 'todas'}
            onClick={() => onSelect(option.id)}
            className={`flex-1 min-w-[60px] px-2 sm:px-3 py-2 rounded-lg text-xs sm:text-sm font-medium transition-all duration-200 truncate ${
              isSelected 
                ? 'bg-gray-700 text-white' 
                : 'text-gray-400 hover:text-gray-200 hover:bg-gray-700/50'
            }`}
            data-testid={`subject-toggle-${option.id || 'todas'}`}
          >
            {option.name}
          </button>
        );
      })}
    </div>
  );
};

// Subject Comparison - Visual bars matching profesor style
const SubjectComparisonChart = ({ subjects, onSubjectClick, selectedSubject }) => {
  if (!subjects || subjects.length === 0) return null;

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <BookOpen className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-semibold text-white">Comparación por Asignatura</h3>
        </div>
      </div>
      
      <div className="p-4 space-y-3">
        {subjects.map((subject) => {
          const config = SUBJECT_CONFIG[subject.id] || {};
          const isSelected = selectedSubject === subject.id;
          const Icon = config.icon || BookOpen;
          
          return (
            <button
              key={subject.id}
              onClick={() => onSubjectClick(isSelected ? null : subject.id)}
              className={`w-full text-left transition-all duration-200 rounded-lg p-3 ${
                isSelected 
                  ? 'bg-gray-700 ring-1 ring-amber-500/50' 
                  : 'bg-gray-700/30 hover:bg-gray-700/50'
              }`}
              data-testid={`subject-bar-${subject.id}`}
            >
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2">
                  <Icon className="w-4 h-4" style={{ color: config.color }} />
                  <span className="text-sm font-medium text-white">{subject.name}</span>
                  <span className="text-xs text-gray-500">{subject.submissions_count} entregas</span>
                </div>
                <span className="text-lg font-bold" style={{ color: config.color }}>
                  {subject.average}%
                </span>
              </div>
              
              {/* Progress bar */}
              <div className="w-full bg-gray-600/50 rounded-full h-2 overflow-hidden">
                <div 
                  className="h-full rounded-full transition-all duration-500"
                  style={{ 
                    width: `${subject.average}%`,
                    backgroundColor: config.color
                  }}
                />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
};

// Group Comparison - Shows groups within selected subject
const GroupComparisonChart = ({ groups, selectedSubject }) => {
  const filteredGroups = selectedSubject 
    ? groups.filter(g => g.subject_id === selectedSubject)
    : [];

  if (filteredGroups.length === 0) return null;

  const sortedGroups = [...filteredGroups].sort((a, b) => b.average - a.average);
  const subjectName = SUBJECT_CONFIG[selectedSubject]?.name || selectedSubject;
  const subjectColor = SUBJECT_CONFIG[selectedSubject]?.color || '#06b6d4';

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4" style={{ color: subjectColor }} />
          <h3 className="text-sm font-semibold text-white">
            Grupos de <span style={{ color: subjectColor }}>{subjectName}</span>
          </h3>
        </div>
      </div>
      
      <div className="p-4 space-y-3">
        {sortedGroups.map((group, index) => (
          <div key={group.id} className="bg-gray-700/30 rounded-lg p-3">
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <div 
                  className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white"
                  style={{ backgroundColor: GROUP_COLORS[group.letter] || '#6b7280' }}
                >
                  {group.letter}
                </div>
                <div>
                  <span className="text-sm font-medium text-white">Grupo {group.letter}</span>
                  <span className="text-xs text-gray-500 ml-2">{group.submissions_count} entregas</span>
                </div>
              </div>
              <div className="text-right">
                <span className="text-lg font-bold" style={{ color: GROUP_COLORS[group.letter] }}>
                  {group.average}%
                </span>
                {index === 0 && sortedGroups.length > 1 && (
                  <span className="text-xs text-emerald-400 ml-2">★ Mejor</span>
                )}
              </div>
            </div>
            
            {/* Progress bar */}
            <div className="w-full bg-gray-600/50 rounded-full h-2 overflow-hidden">
              <div 
                className="h-full rounded-full transition-all duration-500"
                style={{ 
                  width: `${group.average}%`,
                  backgroundColor: GROUP_COLORS[group.letter] || '#6b7280'
                }}
              />
            </div>
          </div>
        ))}
        
        {/* Difference summary */}
        {sortedGroups.length >= 2 && (
          <div className="text-center pt-2 border-t border-gray-700/50">
            <p className="text-xs text-gray-400">
              Diferencia entre grupos:{' '}
              <span className="text-amber-400 font-medium">
                {(sortedGroups[0].average - sortedGroups[sortedGroups.length - 1].average).toFixed(1)}%
              </span>
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

// All Groups Overview - When no subject is selected
const AllGroupsOverview = ({ groups }) => {
  if (!groups || groups.length === 0) return null;

  // Group by subject
  const groupedBySubject = {};
  groups.forEach(g => {
    const subjectId = g.subject_id;
    if (!groupedBySubject[subjectId]) {
      groupedBySubject[subjectId] = [];
    }
    groupedBySubject[subjectId].push(g);
  });

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
      <div className="px-4 py-3 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <Users className="w-4 h-4 text-indigo-400" />
          <h3 className="text-sm font-semibold text-white">Resumen de Grupos por Asignatura</h3>
        </div>
      </div>
      
      <div className="p-4">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {Object.entries(groupedBySubject).map(([subjectId, subjectGroups]) => {
            const config = SUBJECT_CONFIG[subjectId] || { name: subjectId, color: '#6b7280' };
            const sortedGroups = [...subjectGroups].sort((a, b) => b.average - a.average);
            const avgOfGroups = sortedGroups.reduce((sum, g) => sum + g.average, 0) / sortedGroups.length;
            
            return (
              <div 
                key={subjectId}
                className="bg-gray-700/30 rounded-lg p-3"
              >
                <div className="flex items-center gap-2 mb-3">
                  <div className="w-1 h-4 rounded-full" style={{ backgroundColor: config.color }} />
                  <span className="text-xs font-medium" style={{ color: config.color }}>
                    {config.name}
                  </span>
                </div>
                
                <div className="space-y-2">
                  {sortedGroups.map((group) => (
                    <div key={group.id} className="flex items-center justify-between">
                      <div className="flex items-center gap-1.5">
                        <div 
                          className="w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-bold text-white"
                          style={{ backgroundColor: GROUP_COLORS[group.letter] || '#6b7280' }}
                        >
                          {group.letter}
                        </div>
                        <span className="text-xs text-gray-300">Grupo {group.letter}</span>
                      </div>
                      <span className="text-xs font-medium" style={{ color: GROUP_COLORS[group.letter] }}>
                        {group.average}%
                      </span>
                    </div>
                  ))}
                </div>
                
                <div className="mt-2 pt-2 border-t border-gray-600/50">
                  <p className="text-xs text-gray-500">
                    Promedio: <span className="text-white font-medium">{avgOfGroups.toFixed(1)}%</span>
                  </p>
                </div>
              </div>
            );
          })}
        </div>
        
        <p className="text-xs text-gray-500 text-center mt-3">
          Selecciona una asignatura arriba para ver el detalle de sus grupos
        </p>
      </div>
    </div>
  );
};

// Progress Summary Component with AI
const ProgressSummary = ({ selectedSubject }) => {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchSummary();
  }, [selectedSubject]);

  const fetchSummary = async () => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/professor/progress-summary`);
      if (!response.ok) throw new Error('Error al cargar resumen');
      const result = await response.json();
      setSummary(result);
    } catch (err) {
      console.error('Error fetching summary:', err);
      setSummary({ summary: 'No se pudo generar el análisis.', has_data: false });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden" data-testid="progress-summary">
      <div className="px-4 py-3 border-b border-gray-700/50">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-amber-400" />
          <h3 className="text-sm font-semibold text-white">Análisis Inteligente</h3>
        </div>
      </div>
      <div className="p-4">
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-400">
            <Loader2 className="w-4 h-4 animate-spin" />
            <span>Analizando el progreso del grupo...</span>
          </div>
        ) : (
          <p className="text-sm text-gray-300 leading-relaxed">
            {summary?.summary}
          </p>
        )}
      </div>
    </div>
  );
};

// Most Missed Question Row - Clean style
const MissedQuestionRow = ({ question, index }) => {
  const errorColor = question.error_rate >= 70 ? 'text-red-400' : question.error_rate >= 50 ? 'text-amber-400' : 'text-yellow-400';
  const bgColor = question.error_rate >= 70 ? 'bg-red-500/10' : question.error_rate >= 50 ? 'bg-amber-500/10' : 'bg-yellow-500/10';
  
  return (
    <div className="flex items-start gap-3 py-3" data-testid={`missed-question-${index}`}>
      <div className={`flex-shrink-0 w-10 h-10 rounded-lg flex items-center justify-center ${bgColor}`}>
        <span className={`text-sm font-bold ${errorColor}`}>
          {Math.round(question.error_rate)}%
        </span>
      </div>
      
      <div className="flex-1 min-w-0">
        <p className="text-sm text-gray-200 leading-relaxed">{question.question}</p>
        <div className="flex items-center gap-2 mt-1">
          <span className="text-xs text-gray-500 flex items-center gap-1">
            <BookOpen className="w-3 h-3" />
            {question.topic}
          </span>
          <span className="text-xs text-gray-500">
            • {question.total_errors}/{question.total_attempts} fallaron
          </span>
        </div>
      </div>
    </div>
  );
};

// Stat Card - Matches profesor style
const StatCard = ({ icon: Icon, label, value, color = 'default' }) => {
  const colorClasses = {
    default: 'text-white',
    emerald: 'text-emerald-400',
    red: 'text-red-400'
  };

  return (
    <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-4">
      <div className="flex items-center gap-2 mb-1">
        <Icon className="w-4 h-4 text-gray-400" />
        <span className="text-xs text-gray-500">{label}</span>
      </div>
      <p className={`text-xl font-bold ${colorClasses[color]}`}>
        {value}
      </p>
    </div>
  );
};

// Main Component
const ProfesorResultadosDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedSubject, setSelectedSubject] = useState(null);

  useEffect(() => {
    fetchDashboard();
  }, [selectedSubject]);

  const fetchDashboard = async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/professor/results-dashboard`;
      const params = new URLSearchParams();
      if (selectedSubject) params.append('subject_id', selectedSubject);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await fetch(url);
      if (!response.ok) throw new Error('Error al cargar dashboard');
      const result = await response.json();
      setData(result);
    } catch (err) {
      console.error('Error fetching dashboard:', err);
      setError('No se pudo cargar el dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleSubjectClick = (subjectId) => {
    setSelectedSubject(subjectId);
  };

  if (loading && !data) {
    return (
      <div className="h-full flex items-center justify-center" data-testid="dashboard-loading">
        <Loader2 className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="h-full flex flex-col items-center justify-center text-gray-400" data-testid="dashboard-error">
        <BarChart3 className="w-10 h-10 mb-3 text-gray-600" />
        <p className="text-sm">{error}</p>
      </div>
    );
  }

  const stats = data?.overall_stats;
  const allSubjects = data?.subjects || [];
  const allGroups = data?.groups || [];

  return (
    <div className="h-full overflow-y-auto" data-testid="profesor-resultados-dashboard">
      <div className="max-w-4xl mx-auto p-6 space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-indigo-500/20 rounded-lg">
            <BarChart3 className="w-5 h-5 text-indigo-400" />
          </div>
          <div>
            <h1 className="text-lg font-bold text-white">Resultados del Grupo</h1>
            <p className="text-xs text-gray-500">
              {stats?.total_submissions || 0} entregas • {stats?.total_challenges || 0} retos
            </p>
          </div>
        </div>

        {/* Subject Toggle */}
        <SubjectToggle 
          selectedSubject={selectedSubject}
          onSelect={setSelectedSubject}
        />

        {/* Stats Row */}
        {stats && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <StatCard 
              icon={Award} 
              label="Promedio General" 
              value={`${stats.average_score}%`}
            />
            <StatCard 
              icon={TrendingUp} 
              label="Mejor Calificación" 
              value={`${stats.best_score}%`}
              color="emerald"
            />
            <StatCard 
              icon={TrendingDown} 
              label="Calificación Mínima" 
              value={`${stats.lowest_score}%`}
              color="red"
            />
          </div>
        )}

        {/* Subject Comparison - When "Todas" is selected */}
        {!selectedSubject && allSubjects.length > 0 && (
          <SubjectComparisonChart 
            subjects={allSubjects}
            onSubjectClick={handleSubjectClick}
            selectedSubject={selectedSubject}
          />
        )}

        {/* When subject is selected, show its groups */}
        {selectedSubject && (
          <GroupComparisonChart 
            groups={allGroups}
            selectedSubject={selectedSubject}
          />
        )}

        {/* All Groups Overview - When "Todas" is selected */}
        {!selectedSubject && allGroups.length > 0 && (
          <AllGroupsOverview groups={allGroups} />
        )}

        {/* AI Progress Summary */}
        <ProgressSummary selectedSubject={selectedSubject} />

        {/* Most Missed Questions */}
        {data?.most_missed_questions && data.most_missed_questions.length > 0 && (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 overflow-hidden">
            <div className="px-4 py-3 border-b border-gray-700/50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-4 h-4 text-amber-400" />
                <h3 className="text-sm font-semibold text-white">Preguntas con Mayor Dificultad</h3>
              </div>
            </div>
            <div className="px-4 divide-y divide-gray-700/50">
              {data.most_missed_questions.slice(0, 5).map((question, idx) => (
                <MissedQuestionRow key={idx} question={question} index={idx} />
              ))}
            </div>
          </div>
        )}

        {/* Empty state for no missed questions */}
        {(!data?.most_missed_questions || data.most_missed_questions.length === 0) && stats?.total_submissions > 0 && (
          <div className="bg-emerald-500/10 rounded-xl border border-emerald-500/30 p-4">
            <div className="flex items-center gap-2 text-emerald-400">
              <Target className="w-4 h-4" />
              <span className="text-sm">¡Excelente! No hay preguntas con alto índice de error.</span>
            </div>
          </div>
        )}

        {/* Empty State */}
        {(!stats || stats.total_submissions === 0) && (
          <div className="bg-gray-800/50 rounded-xl border border-gray-700/50 p-8 text-center">
            <div className="w-12 h-12 bg-gray-700/50 rounded-full flex items-center justify-center mx-auto mb-3">
              <BarChart3 className="w-6 h-6 text-gray-500" />
            </div>
            <h3 className="text-sm font-medium text-white mb-1">Sin resultados aún</h3>
            <p className="text-xs text-gray-500 max-w-xs mx-auto">
              Los alumnos deben completar algunos retos para ver estadísticas.
            </p>
          </div>
        )}

        {/* Footer spacing */}
        <div className="h-4"></div>
      </div>
    </div>
  );
};

export default ProfesorResultadosDashboard;
